import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Stat, StatLabel, StatNumber, Button, Icon } from "@chakra-ui/react";
import { FaCheckCircle, FaArrowRight } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { useNavigate } from "react-router-dom";

const About: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/about"
        fallbackTitle="About One Group Australia"
        fallbackDescription="One Group Australia designs life infrastructure — integrating water, solar, finance, and property through The Prosperity Engine™."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              About One Group Australia
            </Text>
            <Heading size="2xl" fontWeight="bold">
              We Design Systems. Not Transactions.
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              One Group Australia integrates water, solar, finance, and property into a single long-term strategy — The Prosperity Engine™.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Mission, Vision, Values Section */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
          <Box
            p={8}
            bg="white"
            borderRadius="lg"
            boxShadow="lg"
            borderTop="4px solid"
            borderColor={brandConfig.colors.primary}
          >
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>
              Our Mission
            </Heading>
            <Text color="gray.600">
              To help Australians take control of the systems that shape their future — their health, their energy, their financial structure, and their assets — through strategic integration, long-term thinking, and disciplined execution.
            </Text>
          </Box>

          <Box
            p={8}
            bg="white"
            borderRadius="lg"
            boxShadow="lg"
            borderTop="4px solid"
            borderColor={brandConfig.colors.primary}
          >
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>
              Our Vision
            </Heading>
            <Text color="gray.600">
              To become Australia's most trusted integrated prosperity platform — connecting water quality, solar efficiency, finance structure, and property growth into one coordinated strategy that compounds over decades.
            </Text>
          </Box>

          <Box
            p={8}
            bg="white"
            borderRadius="lg"
            boxShadow="lg"
            borderTop="4px solid"
            borderColor="#C2A878"
          >
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>
              Our Values
            </Heading>
            <VStack align="start" spacing={2}>
              <Text color="gray.600" display="flex" alignItems="center">
                <Icon as={FaCheckCircle} color="#C2A878" mr={2} />
                Integrity in every interaction
              </Text>
              <Text color="gray.600" display="flex" alignItems="center">
                <Icon as={FaCheckCircle} color="#C2A878" mr={2} />
                Structured, long-term thinking
              </Text>
              <Text color="gray.600" display="flex" alignItems="center">
                <Icon as={FaCheckCircle} color="#C2A878" mr={2} />
                Education before promotion
              </Text>
              <Text color="gray.600" display="flex" alignItems="center">
                <Icon as={FaCheckCircle} color="#C2A878" mr={2} />
                Client outcomes over commission
              </Text>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Stats Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <Heading size="xl" textAlign="center" mb={12} color={brandConfig.colors.primary}>
            The Engine in Numbers
          </Heading>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            <Stat textAlign="center" p={6} bg="white" borderRadius="lg" boxShadow="md">
              <StatNumber fontSize="4xl" color={brandConfig.colors.primary} fontWeight="bold">
                15+
              </StatNumber>
              <StatLabel fontSize="md" color="gray.600" mt={2}>
                Years Experience
              </StatLabel>
            </Stat>

            <Stat textAlign="center" p={6} bg="white" borderRadius="lg" boxShadow="md">
              <StatNumber fontSize="4xl" color={brandConfig.colors.primary} fontWeight="bold">
                5,000+
              </StatNumber>
              <StatLabel fontSize="md" color="gray.600" mt={2}>
                Clients Served
              </StatLabel>
            </Stat>

            <Stat textAlign="center" p={6} bg="white" borderRadius="lg" boxShadow="md">
              <StatNumber fontSize="4xl" color={brandConfig.colors.primary} fontWeight="bold">
                10,000+
              </StatNumber>
              <StatLabel fontSize="md" color="gray.600" mt={2}>
                Projects Delivered
              </StatLabel>
            </Stat>

            <Stat textAlign="center" p={6} bg="white" borderRadius="lg" boxShadow="md">
              <StatNumber fontSize="4xl" color={brandConfig.colors.primary} fontWeight="bold">
                50+
              </StatNumber>
              <StatLabel fontSize="md" color="gray.600" mt={2}>
                Team Members
              </StatLabel>
            </Stat>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Our People Section */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={8}>
          <Heading size="xl" textAlign="center" color={brandConfig.colors.primary}>
            Our People
          </Heading>
          <Text fontSize="lg" textAlign="center" color="gray.600" maxW="3xl">
            Each division is led by specialists who live and work in the areas they advise on. Our property advisers, finance brokers, solar engineers, and water specialists operate as one coordinated group — not four separate businesses.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full" maxW="3xl">
            <Box p={6} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
              <Text fontWeight="700" color={brandConfig.colors.primary} mb={1}>One Property Australia</Text>
              <Text fontSize="sm" color="gray.600">Investment strategy and portfolio specialists</Text>
            </Box>
            <Box p={6} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
              <Text fontWeight="700" color={brandConfig.colors.primary} mb={1}>One Finance Australia</Text>
              <Text fontSize="sm" color="gray.600">Independent mortgage and asset finance brokers</Text>
            </Box>
            <Box p={6} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
              <Text fontWeight="700" color={brandConfig.colors.primary} mb={1}>One Solar Australia</Text>
              <Text fontSize="sm" color="gray.600">CEC-accredited solar engineers and installation specialists</Text>
            </Box>
            <Box p={6} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
              <Text fontWeight="700" color={brandConfig.colors.primary} mb={1}>One Water Australia</Text>
              <Text fontSize="sm" color="gray.600">Research-backed water quality and filtration advisers</Text>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>

      {/* CTA Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">
              Ready to Build Your Prosperity Engine™?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="rgba(255, 255, 255, 0.9)">
              Explore how water, solar, finance, and property work together as a single long-term system. Begin with any division — or start with a group consultation.
            </Text>
            <Button
              size="lg"
              bg="#C2A878"
              color={brandConfig.colors.primary}
              _hover={{ bg: "#b09060", transform: "translateY(-2px)" }}
              rightIcon={<Icon as={FaArrowRight} />}
              onClick={() => navigate("/contact")}
              transition="all 0.2s"
              fontWeight="bold"
            >
              Begin a Conversation
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default About;
